import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import {
  Calendar,
  FileText,
  User,
  Clock,
  Check,
  X,
  Plus,
} from 'lucide-react';

type RequestTab = 'vacation' | 'documents' | 'personal' | 'history';
type DocumentType = 'INCOME_STATEMENT' | 'EMPLOYMENT_LETTER' | 'SALARY_STATEMENT' | 'OTHER';

export function EmployeeHrRequests() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<RequestTab>('vacation');
  const [showVacationModal, setShowVacationModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  
  // Vacation form state
  const [vacationStartDate, setVacationStartDate] = useState('');
  const [vacationEndDate, setVacationEndDate] = useState('');
  const [vacationNotes, setVacationNotes] = useState('');
  
  // Document form state
  const [documentType, setDocumentType] = useState<DocumentType>('INCOME_STATEMENT');
  const [documentNotes, setDocumentNotes] = useState('');

  // Fetch vacation balance
  const { data: vacationBalance } = useQuery({
    queryKey: ['employee', 'hr', 'vacation-balance'],
    queryFn: async () => {
      const response = await api.get('/banking/employee/hr-requests/vacation-balance');
      return response.data;
    },
  });

  // Fetch request history
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['employee', 'hr', 'history'],
    queryFn: async () => {
      const response = await api.get('/banking/employee/hr-requests/history');
      return response.data;
    },
  });


  // Request vacation mutation
  const requestVacationMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/banking/employee/hr-requests/vacation', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee', 'hr'] });
      setShowVacationModal(false);
      setVacationStartDate('');
      setVacationEndDate('');
      setVacationNotes('');
    },
  });

  // Request document mutation
  const requestDocumentMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/banking/employee/hr-requests/document', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee', 'hr'] });
      setShowDocumentModal(false);
      setDocumentType('INCOME_STATEMENT');
      setDocumentNotes('');
    },
  });

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(date));
  };

  const calculateDays = () => {
    if (!vacationStartDate || !vacationEndDate) return 0;
    const start = new Date(vacationStartDate);
    const end = new Date(vacationEndDate);
    const diff = end.getTime() - start.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED': return 'bg-green-500/20 text-green-400';
      case 'PENDING': return 'bg-yellow-500/20 text-yellow-400';
      case 'REJECTED': return 'bg-red-500/20 text-red-400';
      case 'COMPLETED': return 'bg-blue-500/20 text-blue-400';
      default: return 'bg-surface-500/20 text-surface-400';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'APPROVED': return 'Aprovado';
      case 'PENDING': return 'Pendente';
      case 'REJECTED': return 'Rejeitado';
      case 'COMPLETED': return 'Concluído';
      default: return status;
    }
  };

  const getRequestTypeLabel = (type: string) => {
    switch (type) {
      case 'VACATION': return 'Férias';
      case 'DOCUMENT': return 'Documento';
      case 'PERSONAL_DATA': return 'Dados Pessoais';
      default: return type;
    }
  };

  const tabs = [
    { id: 'vacation' as RequestTab, label: 'Férias', icon: <Calendar size={18} /> },
    { id: 'documents' as RequestTab, label: 'Documentos', icon: <FileText size={18} /> },
    { id: 'personal' as RequestTab, label: 'Dados Pessoais', icon: <User size={18} /> },
    { id: 'history' as RequestTab, label: 'Histórico', icon: <Clock size={18} /> },
  ];

  const documentTypes = [
    { value: 'INCOME_STATEMENT', label: 'Declaração de Rendimentos' },
    { value: 'EMPLOYMENT_LETTER', label: 'Carta de Emprego' },
    { value: 'SALARY_STATEMENT', label: 'Comprovante de Salário' },
    { value: 'OTHER', label: 'Outro' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Solicitações RH</h1>
        <p className="text-surface-400">Férias, documentos e atualizações cadastrais</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap
              transition-colors
              ${activeTab === tab.id
                ? 'bg-primary-500 text-white'
                : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
              }
            `}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Vacation Tab */}
      {activeTab === 'vacation' && (
        <div className="space-y-6">
          {/* Vacation Balance */}
          <Card className="bg-gradient-to-br from-primary-600 to-primary-800 border-0">
            <div className="p-6">
              <p className="text-primary-200 mb-2">Saldo de férias disponível</p>
              <p className="text-3xl font-bold text-white">
                {vacationBalance?.availableDays || 0} dias
              </p>
              <p className="text-sm text-primary-200 mt-2">
                Período aquisitivo: {vacationBalance?.acquisitivePeriod || '-'}
              </p>
            </div>
          </Card>

          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">Solicitar Férias</h2>
                <Button variant="primary" onClick={() => setShowVacationModal(true)}>
                  <Plus size={18} className="mr-2" />
                  Nova Solicitação
                </Button>
              </div>

              <p className="text-surface-400">
                Solicite suas férias com antecedência mínima de 30 dias.
              </p>
            </div>
          </Card>
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Solicitar Documento</h2>
              <Button variant="primary" onClick={() => setShowDocumentModal(true)}>
                <Plus size={18} className="mr-2" />
                Nova Solicitação
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {documentTypes.map((doc) => (
                <button
                  key={doc.value}
                  onClick={() => {
                    setDocumentType(doc.value as DocumentType);
                    setShowDocumentModal(true);
                  }}
                  className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                    <FileText size={20} className="text-primary-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{doc.label}</p>
                    <p className="text-xs text-surface-400">Clique para solicitar</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Personal Data Tab */}
      {activeTab === 'personal' && (
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Atualizar Dados Pessoais</h2>
            <p className="text-surface-400 mb-6">
              Para atualizar seus dados cadastrais, entre em contato com o RH.
            </p>

            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-surface-800/50">
                <p className="text-xs text-surface-400 mb-1">E-mail</p>
                <p className="text-sm text-white">Contate o RH para alteração</p>
              </div>
              <div className="p-4 rounded-lg bg-surface-800/50">
                <p className="text-xs text-surface-400 mb-1">Telefone</p>
                <p className="text-sm text-white">Contate o RH para alteração</p>
              </div>
              <div className="p-4 rounded-lg bg-surface-800/50">
                <p className="text-xs text-surface-400 mb-1">Endereço</p>
                <p className="text-sm text-white">Contate o RH para alteração</p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Histórico de Solicitações</h2>

            {historyLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50 animate-pulse">
                    <div className="w-10 h-10 rounded-full bg-surface-700" />
                    <div className="flex-1">
                      <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                      <div className="h-3 w-24 bg-surface-700 rounded" />
                    </div>
                    <div className="h-5 w-20 bg-surface-700 rounded" />
                  </div>
                ))}
              </div>
            ) : historyData?.requests?.length > 0 ? (
              <div className="space-y-3">
                {historyData.requests.map((request: any) => (
                  <div
                    key={request.id}
                    className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50"
                  >
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      ${request.status === 'APPROVED' || request.status === 'COMPLETED'
                        ? 'bg-green-500/20'
                        : request.status === 'PENDING'
                        ? 'bg-yellow-500/20'
                        : 'bg-red-500/20'
                      }
                    `}>
                      {request.status === 'APPROVED' || request.status === 'COMPLETED'
                        ? <Check size={20} className="text-green-400" />
                        : request.status === 'PENDING'
                        ? <Clock size={20} className="text-yellow-400" />
                        : <X size={20} className="text-red-400" />
                      }
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">
                        {getRequestTypeLabel(request.type)}
                      </p>
                      <p className="text-xs text-surface-400">
                        Solicitado em {formatDate(request.createdAt)}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(request.status)}`}>
                      {getStatusLabel(request.status)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock size={48} className="mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">Nenhuma solicitação encontrada</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Vacation Modal */}
      <Modal
        isOpen={showVacationModal}
        onClose={() => setShowVacationModal(false)}
        title="Solicitar Férias"
      >
        <div className="space-y-4">
          <Input
            label="Data de início"
            type="date"
            value={vacationStartDate}
            onChange={(e) => setVacationStartDate(e.target.value)}
          />
          <Input
            label="Data de término"
            type="date"
            value={vacationEndDate}
            onChange={(e) => setVacationEndDate(e.target.value)}
          />

          {vacationStartDate && vacationEndDate && (
            <div className="p-3 rounded-lg bg-surface-800">
              <p className="text-sm text-surface-400">
                Período: <span className="text-white font-medium">{calculateDays()} dias</span>
              </p>
            </div>
          )}

          <Input
            label="Observações (opcional)"
            placeholder="Informações adicionais..."
            value={vacationNotes}
            onChange={(e) => setVacationNotes(e.target.value)}
          />

          <div className="flex gap-3 pt-4">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => setShowVacationModal(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={() => requestVacationMutation.mutate({
                startDate: vacationStartDate,
                endDate: vacationEndDate,
                notes: vacationNotes,
              })}
              disabled={!vacationStartDate || !vacationEndDate || requestVacationMutation.isPending}
            >
              {requestVacationMutation.isPending ? 'Enviando...' : 'Solicitar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Document Modal */}
      <Modal
        isOpen={showDocumentModal}
        onClose={() => setShowDocumentModal(false)}
        title="Solicitar Documento"
      >
        <div className="space-y-4">
          <Select
            label="Tipo de documento"
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value as DocumentType)}
            options={documentTypes}
          />

          <Input
            label="Observações (opcional)"
            placeholder="Informações adicionais..."
            value={documentNotes}
            onChange={(e) => setDocumentNotes(e.target.value)}
          />

          <div className="flex gap-3 pt-4">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => setShowDocumentModal(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={() => requestDocumentMutation.mutate({
                type: documentType,
                notes: documentNotes,
              })}
              disabled={requestDocumentMutation.isPending}
            >
              {requestDocumentMutation.isPending ? 'Enviando...' : 'Solicitar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default EmployeeHrRequests;
